/**
 * The one place the application talks to the API.
 *
 * Every request goes through here so that four cross-cutting concerns are
 * handled once rather than at each call site:
 *
 * 1. **The bearer token**, injected from the in-memory store.
 * 2. **Refresh, single-flight.** When a token expires, several requests
 *    discover it at once. Without coordination each would start its own
 *    refresh, and — because refresh tokens rotate — the second would present a
 *    token the first had just retired, which this backend correctly treats as
 *    theft and punishes by killing the session. So concurrent refreshes share
 *    one promise. This is the single most important thing in this file.
 * 3. **The envelope**, unwrapped once so callers see their payload.
 * 4. **Errors**, turned into typed exceptions a UI can branch on.
 */

import { ApiError, NetworkError, toApiError } from "./errors";
import type { Session, TokenStore } from "./tokens";

/** Everything the client needs to reach the API. */
export interface ClientConfig {
  /** Origin of the API, without a trailing slash. */
  baseUrl: string;
  /** Tenant slug, used to build the anonymous routes. */
  tenantId: string;
  tokens: TokenStore;
  /**
   * Called when the session cannot be recovered. The application sends the
   * user to sign in; the client does not navigate, because a library that
   * changes the URL is a library you cannot test.
   */
  onSessionLost?: () => void;
  /** Refresh this many seconds before expiry. See `isExpiringWithin`. */
  refreshLeadSeconds?: number;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * A multipart body, sent as-is.
   *
   * Mutually exclusive with `body`. It exists because `Content-Type` must NOT
   * be set for `FormData` — the browser generates the multipart boundary and
   * writes the header itself, and a hand-set `multipart/form-data` produces a
   * request with no boundary that every server rejects as malformed.
   *
   * Kept here rather than in the calling feature so that uploads inherit the
   * refresh-and-retry, the session-lost notification and the error mapping
   * that every other call gets. A feature reaching for `fetch` directly would
   * quietly opt out of all three.
   */
  formData?: FormData;
  /** Skip the bearer token — for the anonymous and semi-authenticated routes. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

/** The success envelope the API wraps every payload in. */
interface SuccessEnvelope<T> {
  status: { code: number };
  response: { data: T };
}

const DEFAULT_REFRESH_LEAD_SECONDS = 60;

export class ApiClient {
  readonly #config: ClientConfig;
  /**
   * The in-flight refresh, if any. Holding the promise rather than a boolean is
   * what lets every waiting caller await the SAME result instead of racing.
   */
  #refreshing: Promise<Session | null> | null = null;
  readonly #sessionLostListeners = new Set<() => void>();

  constructor(config: ClientConfig) {
    this.#config = config;
  }

  // --- Session ---------------------------------------------------------------

  /**
   * Adopt the session a sign-in or an MFA verification returned.
   *
   * Here rather than in the caller so that the wire shape — snake_case, an ISO
   * timestamp — is decoded in exactly one place. Every screen that signs a
   * user in would otherwise repeat it, and the one that parsed the expiry
   * wrongly would produce a session that silently never refreshes.
   *
   * The refresh token in the payload is deliberately ignored in the browser:
   * the server also sets it as an `HttpOnly` cookie, and storing the copy
   * anywhere script can read would undo the point of that.
   */
  adoptSession(payload: {
    access_token: string;
    access_token_expires_at: string;
    lineage_id: string;
  }): void {
    this.#config.tokens.set({
      accessToken: payload.access_token,
      expiresAt: new Date(payload.access_token_expires_at),
      lineageId: payload.lineage_id,
    });
  }

  /** Forget the session locally. Does not call the API. */
  clearSession(): void {
    this.#config.tokens.clear();
  }

  /** The current session, or null. */
  get session(): Session | null {
    return this.#config.tokens.session;
  }

  /**
   * Be told when the session is lost — a refresh that failed, a 401 anywhere,
   * a revocation from another device.
   *
   * Returns an unsubscribe function. Several listeners are supported because
   * more than one part of the interface legitimately cares: the router has to
   * redirect, and a long-running view has to stop polling.
   */
  onSessionLost(listener: () => void): () => void {
    this.#sessionLostListeners.add(listener);
    return () => {
      this.#sessionLostListeners.delete(listener);
    };
  }

  #notifySessionLost(): void {
    this.#config.onSessionLost?.();
    for (const listener of this.#sessionLostListeners) listener();
  }

  /** Path for an anonymous route: `/api/v1/iam/{tenantId}/…`. */
  anonymousPath(tail: string): string {
    return `/api/v1/iam/${encodeURIComponent(this.#config.tenantId)}${tail}`;
  }

  /** Path for an authenticated route: `/api/v1/iam/…` — no tenant segment. */
  authenticatedPath(tail: string): string {
    return `/api/v1/iam${tail}`;
  }

  /**
   * Perform a request and return the raw bytes.
   *
   * For the responses that are not JSON: a product photo, a document, an
   * export. It goes through the same refresh-and-retry and the same error
   * mapping as `request`, which is the whole reason it lives here — a caller
   * reaching for `fetch` to load an image would quietly opt out of both, and
   * the failure mode is an image that silently disappears the moment an access
   * token expires.
   *
   * The caller turns the blob into an object URL and revokes it; this method
   * does not, because it has no idea how long the bytes are needed for.
   */
  async requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const response = await this.#authorisedSend(path, options);
    return response.blob();
  }

  /** Perform a request and return the unwrapped payload. */
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.#authorisedSend(path, options);

    if (response.status === 204) return undefined as T;

    const body = (await response.json()) as SuccessEnvelope<T>;
    // Unwrap both levels here, once, so no call site has to know the envelope
    // exists. A response that is not enveloped is returned as-is rather than
    // throwing: it is almost certainly a proxy speaking, and the caller's own
    // error handling is a better place to notice than this one.
    return body?.response?.data ?? (body as unknown as T);
  }

  /**
   * Send, refresh once on a 401, and translate a failure into an `ApiError`.
   *
   * Shared by `request` and `requestBlob` so the two cannot drift on the part
   * that matters: which failures clear the session.
   */
  async #authorisedSend(path: string, options: RequestOptions): Promise<Response> {
    if (!options.anonymous) await this.#ensureFreshToken();

    let response = await this.#send(path, options);

    // A 401 on a request we thought was authenticated means the token died
    // between the freshness check and arrival — a clock skew, a revocation, a
    // slow request. One refresh-and-retry, never a loop: if the retry also
    // fails, the session is genuinely gone.
    if (response.status === 401 && !options.anonymous) {
      const session = await this.#refresh();
      if (session) response = await this.#send(path, options);
    }

    if (!response.ok) {
      const error = await toApiError(response);
      if (error.isAuthenticationFailure && !options.anonymous) {
        this.#config.tokens.clear();
        this.#notifySessionLost();
      }
      throw error;
    }

    return response;
  }

  /**
   * Upload a multipart body, with the same session handling as any other call.
   *
   * `POST` by default because that is what every upload endpoint here takes;
   * pass `method` for the rare `PUT`.
   */
  upload<T>(path: string, form: FormData, method: "POST" | "PUT" = "POST"): Promise<T> {
    return this.request<T>(path, { method, formData: form });
  }

  async #send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: "application/json" };
    // Deliberately not set for FormData — see `RequestOptions.formData`.
    if (options.body !== undefined) headers["Content-Type"] = "application/json";

    const token = this.#config.tokens.accessToken;
    if (!options.anonymous && token) headers.Authorization = `Bearer ${token}`;

    try {
      return await fetch(`${this.#config.baseUrl}${path}`, {
        method: options.method ?? "GET",
        headers,
        // `null`, not `undefined`: `exactOptionalPropertyTypes` is on, and
        // RequestInit types this as `BodyInit | null`.
        body:
          options.formData ?? (options.body === undefined ? null : JSON.stringify(options.body)),
        // Required for the refresh cookie to be sent at all, and the reason
        // the API's CORS configuration lists exact origins rather than `*`:
        // a wildcard origin and credentials are mutually exclusive.
        credentials: "include",
        signal: options.signal ?? null,
      });
    } catch (cause) {
      // fetch rejects only on a network-level failure; every HTTP status
      // resolves. So anything here really is "the request did not arrive".
      throw new NetworkError(cause);
    }
  }

  /** Refresh proactively if the token is close to expiry. */
  async #ensureFreshToken(): Promise<void> {
    const lead = this.#config.refreshLeadSeconds ?? DEFAULT_REFRESH_LEAD_SECONDS;
    const tokens = this.#config.tokens;
    if (tokens.session && !tokens.isExpiringWithin(lead)) return;
    await this.#refresh();
  }

  /**
   * Exchange the refresh token, coordinating concurrent callers.
   *
   * The single-flight guard is the point. Refresh tokens rotate, so two
   * simultaneous refreshes would mean the second presenting a token the first
   * had just retired — indistinguishable, from the server's side, from a
   * stolen token being replayed. The server would revoke the whole session,
   * and the user would be signed out for the crime of loading two panels at
   * once.
   */
  async #refresh(): Promise<Session | null> {
    this.#refreshing ??= this.#performRefresh().finally(() => {
      this.#refreshing = null;
    });
    return this.#refreshing;
  }

  async #performRefresh(): Promise<Session | null> {
    try {
      // An empty body: the browser cannot read the HttpOnly cookie, so the
      // cookie speaks for it. `credentials: "include"` is what sends it.
      const response = await this.#send(this.authenticatedPath("/token/refresh"), {
        method: "POST",
        body: {},
        anonymous: true,
      });

      if (!response.ok) {
        this.#config.tokens.clear();
        this.#notifySessionLost();
        return null;
      }

      const body = (await response.json()) as SuccessEnvelope<{
        access_token: string;
        access_token_expires_at: string;
        lineage_id: string;
      }>;
      // Decoded by the same method every screen uses, so a wire change is one
      // edit rather than two that can disagree.
      this.adoptSession(body.response.data);
      return this.#config.tokens.session;
    } catch (error) {
      // A network failure during refresh is NOT a lost session — the user is
      // offline, and their credentials are still valid. Clearing here would
      // sign them out for a dropped connection.
      if (error instanceof NetworkError) return null;
      this.#config.tokens.clear();
      this.#notifySessionLost();
      return null;
    }
  }
}

export { ApiError, NetworkError };
